import React from 'react';
import {UserRolesEnum} from 'core/models/users/types';
import {pageUrls} from './pages';
import {IRoute} from './types';

// Lazy load all page components
const AssetList = React.lazy(() => import('pages/assets/asset-list'));
const AssetView = React.lazy(() => import('pages/assets/asset-view'));
const Dashboard = React.lazy(() => import('pages/Dashboard').then(module => ({ default: module.Dashboard })));
const InvoiceList = React.lazy(() => import('pages/Invoices/InvoiceList').then(module => ({ default: module.InvoiceList })));
const InvoiceRetailerList = React.lazy(() => import('pages/Invoices/InvoiceRetailerList').then(module => ({ default: module.InvoiceRetailerList })));
const InvoiceView = React.lazy(() => import('pages/Invoices/InvoiceView').then(module => ({ default: module.InvoiceView })));
const OrderCreate = React.lazy(() => import('pages/Orders/OrderCreate').then(module => ({ default: module.OrderCreate })));
const OrderCreateBulk = React.lazy(() => import('pages/Orders/OrderCreateBulk').then(module => ({ default: module.OrderCreateBulk })));
const OrderList = React.lazy(() => import('pages/Orders/OrderList').then(module => ({ default: module.OrderList })));
const OrderRetailerList = React.lazy(() => import('pages/Orders/OrderRetailerList').then(module => ({ default: module.OrderRetailerList })));
const OrderView = React.lazy(() => import('pages/Orders/OrderView').then(module => ({ default: module.OrderView })));
const ProductCreate = React.lazy(() => import('pages/Products/ProductCreate').then(module => ({ default: module.ProductCreate })));
const ProductGroupCreate = React.lazy(() => import('pages/Products/product-group-create'));
const ProductGroupList = React.lazy(() => import('pages/Products/product-group-list'));
const ProductGroupView = React.lazy(() => import('pages/Products/product-group-view'));
const ProductListAll = React.lazy(() => import('pages/Products/product-list-all'));
const ProductView = React.lazy(() => import('pages/Products/product-view'));
const ReportMarketingAssets = React.lazy(() => import('pages/Reports/report-marketing-assets'));
const Profile = React.lazy(() => import('pages/Profile').then(module => ({ default: module.Profile })));
const RetailerAddressCreate = React.lazy(() => import('pages/Retailers/RetailerAddresses/RetailerAddressCreate').then(module => ({ default: module.RetailerAddressCreate })));
const RetailerAddressList = React.lazy(() => import('pages/Retailers/RetailerAddresses/RetailerAddressList').then(module => ({ default: module.RetailerAddressList })));
const RetailerBuyingProfile = React.lazy(() => import('pages/Retailers/RetailerBuyingProfile').then(module => ({ default: module.RetailerBuyingProfile })));
const RetailerList = React.lazy(() => import('pages/Retailers/RetailerList').then(module => ({ default: module.RetailerList })));
const RetailerShippingProfile = React.lazy(() => import('pages/Retailers/RetailerShippingProfile').then(module => ({ default: module.RetailerShippingProfile })));
const RetailerStoreCreate = React.lazy(() => import('pages/Retailers/RetailerStores/RetailerStoreCreate').then(module => ({ default: module.RetailerStoreCreate })));
const RetailerStoreList = React.lazy(() => import('pages/Retailers/RetailerStores/RetailerStoreList').then(module => ({ default: module.RetailerStoreList })));
const RetailerUserCreate = React.lazy(() => import('pages/Retailers/RetailerUsers/RetailerUserCreate').then(module => ({ default: module.RetailerUserCreate })));
const RetailerUserList = React.lazy(() => import('pages/Retailers/RetailerUsers/RetailerUserList').then(module => ({ default: module.RetailerUserList })));
const RetailerUserView = React.lazy(() => import('pages/Retailers/retailer-user-view'));
const RetailerGeneral = React.lazy(() => import('pages/Retailers/RetailerGeneral').then(module => ({ default: module.RetailerGeneral })));
const ReturnCreate = React.lazy(() => import('pages/Returns/ReturnCreate').then(module => ({ default: module.ReturnCreate })));
const ReturnList = React.lazy(() => import('pages/Returns/ReturnList').then(module => ({ default: module.ReturnList })));
const ReturnListAll = React.lazy(() => import('pages/Returns/ReturnListAll').then(module => ({ default: module.ReturnListAll })));
const ReturnRetailerList = React.lazy(() => import('pages/Returns/ReturnRetailerList').then(module => ({ default: module.ReturnRetailerList })));
const ReturnView = React.lazy(() => import('pages/Returns/ReturnView').then(module => ({ default: module.ReturnView })));
const SettingsGeneral = React.lazy(() => import('pages/Settings/SettingsGeneral').then(module => ({ default: module.SettingsGeneral })));
const SettingsShipping = React.lazy(() => import('pages/Settings/SettingsShipping').then(module => ({ default: module.SettingsShipping })));
const SettingsUserCreate = React.lazy(() => import('pages/Settings/SettingsUsers/SettingsUserCreate').then(module => ({ default: module.SettingsUserCreate })));
const SettingsUserCreateBulk = React.lazy(() => import('pages/Settings/SettingsUsers/SettingsUserCreateBulk').then(module => ({ default: module.SettingsUserCreateBulk })));
const SettingsUserList = React.lazy(() => import('pages/Settings/SettingsUsers/SettingsUserList').then(module => ({ default: module.SettingsUserList })));
const SettingsUserView = React.lazy(() => import('pages/Settings/SettingsUsers/SettingsUserView').then(module => ({ default: module.SettingsUserView })));
const Status = React.lazy(() => import('pages/Status').then(module => ({ default: module.Status })));
const Support = React.lazy(() => import('pages/Support').then(module => ({ default: module.Support })));
const ProductList = React.lazy(() => import('pages/Products/ProductList').then(module => ({ default: module.ProductList })));
const PromoCreate = React.lazy(() => import('pages/PromoBuilder/PromoCreate').then(module => ({ default: module.PromoCreate })));
const PromoBuilderList = React.lazy(() => import('pages/PromoBuilder/PromoList').then(module => ({ default: module.PromoBuilderList })));
const PromoEdit = React.lazy(() => import('pages/PromoBuilder/PromoEdit').then(module => ({ default: module.PromoEdit })));
const TermsAndConditions = React.lazy(() => import('pages/Static/TermsAndConditions').then(module => ({ default: module.TermsAndConditions })));
const SettingsGlobalDiscounts = React.lazy(() => import('pages/Settings/settings-global-discounts'));
const StoreLocatorItemsList = React.lazy(() => import('pages/StoreLocator/StoreLocatorItemsList').then(module => ({ default: module.StoreLocatorItemsList })));
const StoreLocatorItemEdit = React.lazy(() => import('pages/StoreLocator/StoreLocatorItemEdit').then(module => ({ default: module.StoreLocatorItemEdit })));
const StoreLocatorItemCreate = React.lazy(() => import('pages/StoreLocator/StoreLocatorItemCreate').then(module => ({ default: module.StoreLocatorItemCreate })));
const StoreLocatorList = React.lazy(() => import('pages/StoreLocator/StoreLocatorList').then(module => ({ default: module.StoreLocatorList })));
const StoreLocatorCreate = React.lazy(() => import('pages/StoreLocator/StoreLocatorCreate').then(module => ({ default: module.StoreLocatorCreate })));

const routes: IRoute[] = [
  {
    url: pageUrls.profile,
    Component: Profile,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Manager, UserRolesEnum.Returns]
  },
  {
    url: pageUrls.dashboard,
    Component: Dashboard,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.retailerList,
    Component: RetailerList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.retailerGeneral,
    Component: RetailerGeneral,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.retailerBuyingProfile,
    Component: RetailerBuyingProfile,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.retailerShippingProfile,
    Component: RetailerShippingProfile,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.retailerStoreList,
    Component: RetailerStoreList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.retailerStoreCreate,
    Component: RetailerStoreCreate,
    allowedRoles: [UserRolesEnum.Owner]
  },
  {
    url: pageUrls.retailerAddressList,
    Component: RetailerAddressList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.retailerAddressCreate,
    Component: RetailerAddressCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.retailerUserList,
    Component: RetailerUserList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.retailerUserCreate,
    Component: RetailerUserCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.retailerUserView,
    Component: RetailerUserView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.storeLocatorList,
    Component: StoreLocatorList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.storeLocatorCreate,
    Component: StoreLocatorCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.storeLocatorItemsList,
    Component: StoreLocatorItemsList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.storeLocatorItemCreate,
    Component: StoreLocatorItemCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.storeLocatorItemEdit,
    Component: StoreLocatorItemEdit,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.orderRetailerList,
    Component: OrderRetailerList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.orderList,
    Component: OrderList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.orderView,
    Component: OrderView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.orderCreate,
    Component: OrderCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.orderCreateBulk,
    Component: OrderCreateBulk,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.invoiceRetailerList,
    Component: InvoiceRetailerList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.invoiceList,
    Component: InvoiceList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.invoiceView,
    Component: InvoiceView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner]
  },
  {
    url: pageUrls.returnListAll,
    Component: ReturnListAll,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.returnRetailerList,
    Component: ReturnRetailerList,
    allowedRoles: [UserRolesEnum.Sales]
  },
  {
    url: pageUrls.returnList,
    Component: ReturnList,
    allowedRoles: [UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Returns]
  },
  {
    url: pageUrls.returnCreate,
    Component: ReturnCreate,
    allowedRoles: [UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Returns]
  },
  {
    url: pageUrls.returnView,
    Component: ReturnView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Returns]
  },
  {
    url: pageUrls.assetList,
    Component: AssetList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Manager, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.assetView,
    Component: AssetView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Manager, UserRolesEnum.StoreLocatorManager]
  },
  {
    url: pageUrls.productListAll,
    Component: ProductListAll,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.productList,
    Component: ProductList,
    allowedRoles: [UserRolesEnum.Owner]
  },
  {
    url: pageUrls.productCreate,
    Component: ProductCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.productView,
    Component: ProductView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.productGroupList,
    Component: ProductGroupList,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.productGroupCreate,
    Component: ProductGroupCreate,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.productGroupView,
    Component: ProductGroupView,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.reports,
    Component: ReportMarketingAssets,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.status,
    Component: Status,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales]
  },
  {
    url: pageUrls.settingsGeneral,
    Component: SettingsGeneral,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.settingsUserList,
    Component: SettingsUserList,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.settingsUserView,
    Component: SettingsUserView,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.settingsDiscounts,
    Component: SettingsGlobalDiscounts,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.settingsUserCreate,
    Component: SettingsUserCreate,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.settingsUserCreateBulk,
    Component: SettingsUserCreateBulk,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.settingsShipping,
    Component: SettingsShipping,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.support,
    Component: Support,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Manager, UserRolesEnum.Returns]
  },
  {
    url: pageUrls.promoBuilderList,
    Component: PromoBuilderList,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.promoCreate,
    Component: PromoCreate,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.promoEdit,
    Component: PromoEdit,
    allowedRoles: [UserRolesEnum.Admin]
  },
  {
    url: pageUrls.termsAndConditions,
    Component: TermsAndConditions,
    allowedRoles: [UserRolesEnum.Admin, UserRolesEnum.Sales, UserRolesEnum.Owner, UserRolesEnum.Manager, UserRolesEnum.Returns]
  }
];

export default routes;
